/**
 * Analytics vocabulary for the Retro Room feature.
 *
 * Deliberately self-contained: this file has no dependency on `analytics/Tracking.tsx`'s
 * `Category`/`Action` enums, only on the `Tracking` class' `event`/`timing` methods (which
 * accept plain strings). Retro components send events through the same shared `Tracking`
 * instance via `useTracking()` — identical call pattern to planning poker:
 *
 *   const { track } = useTracking();
 *   track.event(RetroCategory.Room, RetroAction.RoomCreated);
 *
 * Every value is prefixed "Retro " so retro events never collide with poker's `Category`/
 * `Action` strings in Firebase Analytics (which uses the action string as the event name).
 * If Retro Room is ever split into its own codebase/app, only this file (and the rest of
 * `features/retro/`) needs to move — no changes to the shared poker enums are required, and
 * no event renaming is needed if it later reports to its own Firebase project.
 */

export enum RetroCategory {
  Room = 'Retro Room',
  Canvas = 'Retro Canvas',
  Template = 'Retro Template',
  Export = 'Retro Export',
  Moderation = 'Retro Moderation',
}

export enum RetroAction {
  // Room lifecycle
  RoomCreated = 'Retro Room Created',
  RoomJoined = 'Retro Room Joined',
  RecentRoomVisited = 'Retro Recent Room Visited',
  PlayerEnrolled = 'Retro Player Enrolled',
  BetaInfoViewed = 'Retro Beta Info Viewed',

  // Canvas — sticky notes and text labels
  StickyCreated = 'Retro Sticky Created',
  StickyDeleted = 'Retro Sticky Deleted',
  StickyColorChanged = 'Retro Sticky Color Changed',
  StickyResized = 'Retro Sticky Resized',
  TextLabelCreated = 'Retro Text Label Created',
  TextLabelDeleted = 'Retro Text Label Deleted',
  MultiSelectUsed = 'Retro Multi-select Used',
  UndoUsed = 'Retro Undo Used',
  RedoUsed = 'Retro Redo Used',
  CopyUsed = 'Retro Copy Used',
  CutUsed = 'Retro Cut Used',
  PasteUsed = 'Retro Paste Used',
  DuplicateUsed = 'Retro Duplicate Used',

  // Template
  TemplatePickerOpened = 'Retro Template Picker Opened',
  TemplateSelected = 'Retro Template Selected',
  TemplateCleared = 'Retro Template Cleared',

  // Export / sharing
  ExportUsed = 'Retro Export Used',
  InviteLinkCopied = 'Retro Invite Link Copied',
  BoardMenuOpened = 'Retro Board Menu Opened',

  // Moderation
  RetroEnded = 'Retro Ended',
  RetroReopened = 'Retro Reopened',
}
